import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Breadcrumbs, EmptyState, PageSkeleton } from "@/components/ui";
import { BulkEditRunDetail } from "@/components/bulk-edits/run/run-detail";
import { formatRunMoment } from "@/components/bulk-edits/run/run-times";
import { startSearchOf } from "@/components/bulk-edits/run/start-search";
import { useBulkEditRun } from "@/hooks/use-bulk-edit-run";
import { usePermissions } from "@/hooks/use-permissions";

export const Route = createFileRoute("/_authenticated/bulk-edits/$runId/")({
  component: BulkEditRunRoute,
});

/**
 * `/bulk-edits/$runId`, one run's page, gated on `bulk_edits:read`. The route
 * reads the run for its breadcrumb and to send a draft — only its stager can
 * read one — to the flow's review step in place of this URL. That decision
 * waits for this page's own read: a cached draft may have been applied since
 * (a second tab, an apply whose answer was lost), so it shows a skeleton until
 * the server answers. A read that fails leaves the draft to the flow, which
 * says why it cannot be shown. Run again opens the review of the draft it
 * staged, carrying this failed run as `from_run` so the review is seeded with
 * this run's note and reason.
 */
function BulkEditRunRoute() {
  const { runId } = Route.useParams();
  const { t, i18n } = useTranslation("bulkEdits");
  const navigate = useNavigate();
  const { can, isLoading } = usePermissions();
  const canRead = can("bulk_edits", "read");
  const runQuery = useBulkEditRun(canRead ? runId : null, { fresh: true });
  const run = runQuery.data;
  const isDraft = run?.id === runId && run.status === "draft";
  const opensFlow = isDraft && runQuery.isFetchedAfterMount;

  useEffect(() => {
    if (!opensFlow) return;
    void navigate({
      to: "/bulk-edits/new",
      search: { step: "review", draft: runId },
      replace: true,
    });
  }, [opensFlow, navigate, runId]);

  if (isLoading || isDraft) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (!canRead) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("denied.read.title")}
          description={t("denied.read.body")}
          variant="inline"
        />
      </div>
    );
  }

  const runCrumb =
    run === undefined
      ? t("run.breadcrumbPending")
      : t("run.breadcrumb", {
          date: formatRunMoment(run.apply_started_at ?? run.created_at, i18n.language),
        });
  const openRun = (id: string) =>
    void navigate({ to: "/bulk-edits/$runId", params: { runId: id } });

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <Breadcrumbs items={[{ label: t("home.title"), to: "/bulk-edits" }, { label: runCrumb }]} />
      <BulkEditRunDetail
        runId={runId}
        onOpenRun={openRun}
        onRunAgain={(draft) =>
          void navigate({
            to: "/bulk-edits/new",
            search: {
              ...startSearchOf(draft, "change"),
              step: "review",
              draft: draft.id,
              from_run: runId,
            },
          })
        }
        onStartFrom={(search) => void navigate({ to: "/bulk-edits/new", search })}
      />
    </div>
  );
}
